import React, { useEffect, useMemo, useRef, useState } from "react";
import { CalendarMenu } from "@/components/Holizontal/CalendarMenu";
import { cn } from "@/lib/utils";
import { apiFetch } from "@/lib/api";
import { helper } from "@/lib/helper";
import { ArrowLeft, ArrowRight } from "lucide-react";
import { motion } from "framer-motion";

type Branch = { id: number; name: string };
type Bdo = { id: number; name: string };

type ApiRow = {
  id: number;
  loan_id?: number;
  loanId?: number;

  CUSTOMER?: string;
  phone_number?: string;
  BRANCH?: string;
  PRODUCT?: string;
  USERNAME?: string;
  USERNAME2?: string;

  total_loan?: number | string;
  loan_amount?: number | string;

  created_at?: string;
};

type CursorMeta = {
  per_page: number;
  next_cursor: string | null;
  prev_cursor: string | null;
  has_more: boolean;
};

type ApiResponse = {
  data: ApiRow[];
  meta: CursorMeta;
  anchor_id: number | null;
  date: string;

  // optional compat if backend still returns these
  branches?: Branch[];
  bdos?: Bdo[];
};

type MetaListResponse<T> = { data: T[] };

const PER_PAGE = 10;
const API_PATH = "/calendar/disbursement";

function parseAmount(a: any) {
  if (a == null) return 0;
  if (typeof a === "number") return a;
  const n = Number(String(a).replace(/[^0-9.]/g, ""));
  return Number.isFinite(n) ? n : 0;
}

function sameDay(a: Date, b: Date) {
  return a.getFullYear() === b.getFullYear() && a.getMonth() === b.getMonth() && a.getDate() === b.getDate();
}

function clampDateToMonth(date: Date, y: number, m: number) {
  const lastDay = new Date(y, m + 1, 0).getDate();
  const d = Math.min(date.getDate(), lastDay);
  return new Date(y, m, d);
}

function addDays(date: Date, days: number) {
  const d = new Date(date);
  d.setDate(d.getDate() + days);
  return d;
}

function monthLabel(m: number) {
  return new Date(2000, m, 1).toLocaleString(undefined, { month: "long" });
}
function weekdayShort(d: Date) {
  return d.toLocaleString(undefined, { weekday: "short" });
}
function dayNumber(d: Date) {
  return d.getDate();
}

function toKey(d: Date) {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  return `${y}-${m}-${day}`;
}

function useIsDesktop(breakpointPx = 768) {
  const [isDesktop, setIsDesktop] = useState(() => window.innerWidth >= breakpointPx);

  useEffect(() => {
    const onResize = () => setIsDesktop(window.innerWidth >= breakpointPx);
    window.addEventListener("resize", onResize);
    return () => window.removeEventListener("resize", onResize);
  }, [breakpointPx]);

  return isDesktop;
}

function useDebounced<T>(value: T, delay = 350) {
  const [v, setV] = useState(value);
  useEffect(() => {
    const t = setTimeout(() => setV(value), delay);
    return () => clearTimeout(t);
  }, [value, delay]);
  return v;
}

const yearsRange = (() => {
  const now = new Date();
  const y = now.getFullYear();
  return Array.from({ length: 7 }, (_, i) => y - 3 + i);
})();

export default function DisbursedCalendarPage() {
  const today = new Date();
  const todayStart = new Date(today.getFullYear(), today.getMonth(), today.getDate()).getTime();
  const isDesktop = useIsDesktop(768);

  const [year, setYear] = useState<number>(today.getFullYear());
  const [month, setMonth] = useState<number>(today.getMonth());
  const [centerDate, setCenterDate] = useState<Date>(clampDateToMonth(today, today.getFullYear(), today.getMonth()));

  const isFuture = (d: Date) => d.getTime() > todayStart;

  // day strip
  const windowSize = isDesktop ? 13 : 3;
  const half = Math.floor(windowSize / 2);
  const step = windowSize;

  const selectedKey = toKey(centerDate);

  // filters
  const [q, setQ] = useState("");
  const qDebounced = useDebounced(q, 350);

  const [branchId, setBranchId] = useState("");
  const [bdoId, setBdoId] = useState("");

  // data
  const [loading, setLoading] = useState(false);
  const [bootLoading, setBootLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const [rows, setRows] = useState<ApiRow[]>([]);

  // meta (branch-first)
  const [branches, setBranches] = useState<Branch[]>([]);
  const [bdos, setBdos] = useState<Bdo[]>([]);
  const [metaLoading, setMetaLoading] = useState(false);
  const [bdoLoading, setBdoLoading] = useState(false);

  // cursor paging
  const [cursor, setCursor] = useState<string | null>(null);
  const [anchorId, setAnchorId] = useState<number | null>(null);
  const [nextCursor, setNextCursor] = useState<string | null>(null);
  const [prevCursor, setPrevCursor] = useState<string | null>(null);

  // scroll target (top of results)
  const tableTopRef = useRef<HTMLDivElement | null>(null);
  const [pendingScrollTop, setPendingScrollTop] = useState(false);

  useEffect(() => {
    if (!pendingScrollTop) return;
    if (loading) return;
    requestAnimationFrame(() => {
      tableTopRef.current?.scrollIntoView({ behavior: "smooth", block: "start" });
    });
    setPendingScrollTop(false);
  }, [pendingScrollTop, loading, rows.length]);

  // month/year
  const onChangeMonth = (m: number) => {
    setMonth(m);
    setCenterDate((prev) => clampDateToMonth(prev, year, m));
  };

  const onChangeYear = (y: number) => {
    setYear(y);
    setCenterDate((prev) => clampDateToMonth(prev, y, month));
  };

  const windowDays = useMemo(() => {
    return Array.from({ length: windowSize }, (_, i) => addDays(centerDate, i - half));
  }, [centerDate, windowSize, half]);

  // ✅ Load branches once
  useEffect(() => {
    let alive = true;

    (async () => {
      try {
        setMetaLoading(true);
        const res = await apiFetch<MetaListResponse<Branch>>("/calendar/meta/branches");
        if (!alive) return;
        setBranches(res.data ?? []);
      } catch {
        if (!alive) return;
        setBranches([]);
      } finally {
        if (alive) setMetaLoading(false);
      }
    })();

    return () => {
      alive = false;
    };
  }, []);

  // ✅ When branch changes: reset + load BDOs (select branch first)
  useEffect(() => {
    let alive = true;

    setBdoId("");
    setBdos([]);

    if (!branchId) return;

    (async () => {
      try {
        setBdoLoading(true);
        const res = await apiFetch<MetaListResponse<Bdo>>(`/calendar/meta/bdos?branch_id=${branchId}`);
        if (!alive) return;
        setBdos(res.data ?? []);
      } catch {
        if (!alive) return;
        setBdos([]);
      } finally {
        if (alive) setBdoLoading(false);
      }
    })();

    return () => {
      alive = false;
    };
  }, [branchId]);

  // reset cursor+anchor when date or filters change
  useEffect(() => {
    setCursor(null);
    setAnchorId(null);
  }, [selectedKey, qDebounced, branchId, bdoId]);

  const queryString = useMemo(() => {
    const params = new URLSearchParams();
    params.set("per_page", String(PER_PAGE));
    params.set("date", selectedKey);

    if (qDebounced.trim()) params.set("q", qDebounced.trim());
    if (branchId) params.set("branch_id", branchId);
    // ✅ only use bdo_id when branch selected
    if (branchId && bdoId) params.set("bdo_id", bdoId);

    if (cursor) params.set("cursor", cursor);
    if (anchorId != null) params.set("anchor_id", String(anchorId));

    return params.toString();
  }, [selectedKey, qDebounced, branchId, bdoId, cursor, anchorId]);

  async function load(isBoot = false) {
    // Future date: show empty & skip fetch
    if (isFuture(centerDate)) {
      setRows([]);
      setNextCursor(null);
      setPrevCursor(null);
      setError(null);
      setLoading(false);
      if (isBoot) setBootLoading(false);
      return;
    }

    if (isBoot) setBootLoading(true);
    setLoading(true);
    setError(null);

    try {
      const json = await apiFetch<ApiResponse>(`${API_PATH}?${queryString}`);

      setRows(json.data ?? []);
      setNextCursor(json.meta?.next_cursor ?? null);
      setPrevCursor(json.meta?.prev_cursor ?? null);
      setAnchorId((prev) => (prev == null ? json.anchor_id ?? null : prev));

      // optional compat (if backend returns these)
      if ((json.branches ?? []).length) setBranches(json.branches ?? []);
      if (branchId && (json.bdos ?? []).length) setBdos(json.bdos ?? []);
    } catch (e: any) {
      setError(e?.message ?? "Failed to load");
      setRows([]);
      setNextCursor(null);
      setPrevCursor(null);
    } finally {
      setLoading(false);
      if (isBoot) setBootLoading(false);
    }
  }

  // boot load once
  useEffect(() => {
    load(true);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // reload on query changes (after boot)
  useEffect(() => {
    if (bootLoading) return;
    load(false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [queryString]);

  const totals = useMemo(() => {
    const total = rows.reduce((s, r) => s + parseAmount(r.total_loan ?? r.loan_amount), 0);
    return { count: rows.length, total };
  }, [rows]);

  const goPrevWindow = () => setCenterDate((d) => addDays(d, -step));
  const goNextWindow = () => {
    const next = addDays(centerDate, step);
    setCenterDate(isFuture(next) ? clampDateToMonth(today, year, month) : next);
  };
  const goToday = () => setCenterDate(clampDateToMonth(today, year, month));

  const resetFilters = () => {
    setQ("");
    setBranchId("");
    setBdoId("");
    setCursor(null);
    setAnchorId(null);
  };

  const goNextPage = () => {
    if (!nextCursor || loading) return;
    setPendingScrollTop(true);
    setCursor(nextCursor);
  };

  const goPrevPage = () => {
    if (!prevCursor || loading) return;
    setPendingScrollTop(true);
    setCursor(prevCursor);
  };

  return (
    <div className="w-full max-w-6xl mx-auto px-3 sm:px-6 py-4 space-y-4">
      <div className="flex items-start justify-between gap-3">
        <div>
          <h1 className="text-2xl font-bold">Disbursed Calendar</h1>
          <p className="text-sm text-muted-foreground">Loans disbursed per day (up to today only).</p>
        </div>
      </div>

      <CalendarMenu />

      {/* ✅ Top loader bar */}
      {loading && (
        <div className="sticky top-0 z-10">
          <div className="h-1 w-full bg-muted overflow-hidden rounded">
            <motion.div
              className="h-full bg-primary"
              initial={{ x: "-100%" }}
              animate={{ x: "100%" }}
              transition={{ repeat: Infinity, duration: 1.0, ease: "linear" }}
            />
          </div>
        </div>
      )}

      {/* Controls + Filters */}
      <div className="bg-card border border-border rounded-xl p-3 sm:p-4 space-y-3">
        {/* Month/Year + Window */}
        <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
          <div className="flex gap-2">
            <select
              value={month}
              onChange={(e) => onChangeMonth(Number(e.target.value))}
              disabled={loading}
              className={cn(
                "h-10 rounded-md border border-border bg-background px-3 text-sm outline-none focus:ring-2 focus:ring-ring",
                loading && "opacity-60 cursor-not-allowed"
              )}
            >
              {Array.from({ length: 12 }, (_, m) => (
                <option key={m} value={m}>
                  {monthLabel(m)}
                </option>
              ))}
            </select>

            <select
              value={year}
              onChange={(e) => onChangeYear(Number(e.target.value))}
              disabled={loading}
              className={cn(
                "h-10 rounded-md border border-border bg-background px-3 text-sm outline-none focus:ring-2 focus:ring-ring",
                loading && "opacity-60 cursor-not-allowed"
              )}
            >
              {yearsRange.map((y) => (
                <option key={y} value={y}>
                  {y}
                </option>
              ))}
            </select>
          </div>

          <div className="flex items-center justify-between sm:justify-end gap-2">
            <button
              onClick={goPrevWindow}
              disabled={loading}
              className={cn(
                "h-10 px-3 rounded-md border border-border bg-background text-sm font-medium",
                loading ? "opacity-60 cursor-not-allowed" : "hover:bg-muted"
              )}
            >
              Prev {step} days
            </button>
            <button
              onClick={goToday}
              disabled={loading}
              className={cn(
                "h-10 px-3 rounded-md border border-border bg-background text-sm font-medium",
                loading ? "opacity-60 cursor-not-allowed" : "hover:bg-muted"
              )}
            >
              Today
            </button>
            <button
              onClick={goNextWindow}
              disabled={loading}
              className={cn(
                "h-10 px-3 rounded-md border border-border bg-background text-sm font-medium",
                loading ? "opacity-60 cursor-not-allowed" : "hover:bg-muted"
              )}
            >
              Next {step} days
            </button>
          </div>
        </div>

        {/* Filters */}
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-2">
          <div className="space-y-1">
            <div className="text-xs text-muted-foreground">Search</div>
            <input
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder="Customer / phone / national id / loan id..."
              disabled={loading}
              className={cn(
                "h-10 w-full rounded-md border border-border bg-background px-3 text-sm outline-none focus:ring-2 focus:ring-ring",
                loading && "opacity-60 cursor-not-allowed"
              )}
            />
          </div>

          <div className="space-y-1">
            <div className="text-xs text-muted-foreground">Branch</div>
            <select
              value={branchId}
              onChange={(e) => setBranchId(e.target.value)}
              disabled={metaLoading || loading}
              className={cn(
                "h-10 w-full rounded-md border border-border bg-background px-3 text-sm outline-none focus:ring-2 focus:ring-ring",
                (metaLoading || loading) && "opacity-60 cursor-not-allowed"
              )}
            >
              <option value="">{metaLoading ? "Loading branches..." : "All Branches"}</option>
              {branches.map((b) => (
                <option key={b.id} value={String(b.id)}>
                  {b.name}
                </option>
              ))}
            </select>
          </div>

          <div className="space-y-1">
            <div className="text-xs text-muted-foreground">BDO</div>
            <select
              value={bdoId}
              onChange={(e) => setBdoId(e.target.value)}
              disabled={!branchId || bdoLoading || loading}
              className={cn(
                "h-10 w-full rounded-md border border-border bg-background px-3 text-sm outline-none focus:ring-2 focus:ring-ring",
                (!branchId || bdoLoading || loading) && "opacity-60 cursor-not-allowed"
              )}
            >
              <option value="">{!branchId ? "Select Branch first" : bdoLoading ? "Loading BDOs..." : "All BDOs"}</option>
              {bdos.map((b) => (
                <option key={b.id} value={String(b.id)}>
                  {b.name}
                </option>
              ))}
            </select>
          </div>
        </div>

        {/* Days strip */}
        <div className="flex justify-center">
          <div className="w-full overflow-x-auto scrollbar-hide">
            <div className="mx-auto min-w-max flex items-center justify-center gap-2 py-1 px-1">
              {windowDays.map((d) => {
                const active = sameDay(d, centerDate);
                const isToday = sameDay(d, today);
                const disabled = isFuture(d);

                return (
                  <button
                    key={toKey(d)}
                    onClick={() => !disabled && setCenterDate(clampDateToMonth(d, year, month))}
                    disabled={disabled || loading}
                    className={cn(
                      "flex flex-col items-center justify-center rounded-xl border text-center transition-all",
                      "w-24 h-16 md:w-16 md:h-16",
                      (disabled || loading) && "opacity-40 cursor-not-allowed",
                      active ? "bg-primary text-primary-foreground border-primary/30 shadow-sm" : "bg-background border-border hover:bg-muted"
                    )}
                  >
                    <span className={cn("text-xs", active ? "" : "text-muted-foreground")}>{weekdayShort(d)}</span>
                    <span className="text-lg font-bold leading-none">{dayNumber(d)}</span>
                    <span
                      className={cn(
                        "text-[11px] leading-none",
                        isToday && !active ? "text-primary font-semibold" : "text-muted-foreground"
                      )}
                    >
                      {isToday ? "Today" : disabled ? "Future" : ""}
                    </span>
                  </button>
                );
              })}
            </div>
          </div>
        </div>

        {/* Summary */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2 pt-1">
          <div className="text-sm">
            <span className="font-semibold">Selected:</span> <span className="text-muted-foreground">{selectedKey}</span>
            {loading && <span className="ml-2 text-muted-foreground">Loading...</span>}
          </div>

          <div className="flex flex-wrap gap-2 text-sm">
            <div className="px-3 py-1 rounded-full bg-muted">
              Disbursed: <span className="font-semibold">{totals.count}</span>
            </div>
            <div className="px-3 py-1 rounded-full bg-muted">
              Total: <span className="font-semibold">{helper.formatMoneyKES(totals.total)}</span>
            </div>

            <button
              onClick={resetFilters}
              disabled={loading}
              className={cn(
                "h-8 px-3 rounded-full border border-border text-sm font-medium",
                loading ? "opacity-60 cursor-not-allowed" : "hover:bg-muted"
              )}
            >
              Reset
            </button>
          </div>
        </div>

        {error && (
          <div className="rounded-lg border border-destructive/40 bg-destructive/10 p-3 text-sm text-destructive">
            {error}
          </div>
        )}
      </div>

      {/* Data section */}
      <div className="bg-card border border-border rounded-xl p-3 sm:p-4 relative">
        {/* ✅ scroll target */}
        <div ref={tableTopRef} />

        {/* ✅ Overlay loader */}
        {loading && (
          <div className="absolute inset-0 z-10 rounded-xl bg-background/60 backdrop-blur-[1px] flex items-center justify-center">
            <div className="flex items-center gap-2 rounded-full border border-border bg-card px-4 py-2 text-sm">
              <motion.span
                className="h-4 w-4 rounded-full border-2 border-muted-foreground border-t-transparent"
                animate={{ rotate: 360 }}
                transition={{ repeat: Infinity, duration: 0.8, ease: "linear" }}
              />
              Loading disbursements...
            </div>
          </div>
        )}

        <div className="flex items-center justify-between mb-3">
          <h2 className="text-base font-semibold">Disbursed on {selectedKey}</h2>
        </div>

        {/* Desktop table */}
        <div className="hidden md:block">
          <div className="overflow-x-auto rounded-lg border border-border">
            <table className="w-full text-sm">
              <thead className="bg-muted/50">
                <tr className="text-left">
                  <th className="px-4 py-3 font-semibold">Branch</th>
                  <th className="px-4 py-3 font-semibold">BDO</th>
                  <th className="px-4 py-3 font-semibold">Customer</th>
                  <th className="px-4 py-3 font-semibold">Product</th>
                  <th className="px-4 py-3 font-semibold">Total Loan</th>
                  <th className="px-4 py-3 font-semibold">Disbursed Date</th>
                </tr>
              </thead>
              <tbody>
                {!loading && rows.length === 0 ? (
                  <tr>
                    <td colSpan={6} className="px-4 py-10 text-center text-muted-foreground">
                      {isFuture(centerDate)
                        ? "Future dates are not available. Select today or earlier."
                        : "No disbursements on this day."}
                    </td>
                  </tr>
                ) : (
                  rows.map((r) => (
                    <tr key={r.id} className="border-t border-border hover:bg-muted/30">
                      <td className="px-4 py-3">{r.BRANCH ?? "-"}</td>
                      <td className="px-4 py-3">
                        <div className="font-medium">{r.USERNAME ?? "-"}</div>
                        {r.USERNAME2 ? <div className="text-xs text-muted-foreground">{r.USERNAME2}</div> : null}
                      </td>
                      <td className="px-4 py-3">
                        <div className="font-medium">{r.CUSTOMER ?? "-"}</div>
                        <div className="text-xs text-muted-foreground">{r.phone_number ?? "-"}</div>
                      </td>
                      <td className="px-4 py-3">
                        <div className="font-medium">{r.PRODUCT ?? "-"}</div>
                        <div className="text-xs text-muted-foreground">{r.loan_id ?? r.loanId ?? "-"}</div>
                      </td>
                      <td className="px-4 py-3">{helper.formatMoneyKES(parseAmount(r.total_loan ?? r.loan_amount))}</td>
                      <td className="px-4 py-3">{helper.compactDateTime(r.created_at)}</td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </div>

        {/* Mobile cards */}
        <div className="md:hidden space-y-2">
          {!loading && rows.length === 0 ? (
            <div className="rounded-lg border border-border p-5 text-center text-sm text-muted-foreground">
              {isFuture(centerDate)
                ? "Future dates are not available. Select today or earlier."
                : "No disbursements on this day."}
            </div>
          ) : (
            rows.map((r) => (
              <div key={r.id} className="w-full text-left rounded-xl border border-border bg-background p-3">
                <div className="flex items-start justify-between gap-3">
                  <div className="min-w-0">
                    <div className="font-semibold truncate">{r.CUSTOMER ?? "Unknown"}</div>
                    <div className="text-xs text-muted-foreground flex flex-wrap gap-x-2 gap-y-1">
                      <span className="truncate">{r.phone_number ?? "-"}</span>
                      <span>•</span>
                      <span className="truncate">{r.BRANCH ?? "-"}</span>
                      <span>•</span>
                      <span className="truncate">{r.PRODUCT ?? "-"}</span>
                    </div>
                  </div>

                  <span className="shrink-0 inline-flex px-2 py-1 rounded-full text-xs border bg-emerald-500/10 border-emerald-500/20 text-emerald-600">
                    Disbursed
                  </span>
                </div>

                <div className="mt-3 grid grid-cols-2 gap-3 text-sm">
                  <div>
                    <div className="text-xs text-muted-foreground">Total</div>
                    <div className="font-bold">{helper.formatMoneyKES(parseAmount(r.total_loan ?? r.loan_amount))}</div>
                  </div>
                  <div>
                    <div className="text-xs text-muted-foreground">Loan ID</div>
                    <div className="font-medium">{r.loan_id ?? r.loanId ?? "-"}</div>
                  </div>

                  <div className="col-span-2">
                    <div className="text-xs text-muted-foreground">BDO</div>
                    <div className="font-medium">{(r.USERNAME ?? "-") + (r.USERNAME2 ? ` & ${r.USERNAME2}` : "")}</div>
                  </div>
                </div>
              </div>
            ))
          )}
        </div>

        {/* Bottom pagination */}
        <div className="mt-4 flex items-center justify-between gap-2">
          <button
            onClick={goPrevPage}
            disabled={!prevCursor || loading}
            className={cn(
              "h-10 px-4 rounded-md border border-border text-sm font-medium inline-flex items-center gap-2",
              !prevCursor || loading ? "opacity-50 cursor-not-allowed" : "hover:bg-muted"
            )}
          >
            <ArrowLeft className="h-4 w-4" />
            Prev
          </button>

          <button
            onClick={goNextPage}
            disabled={!nextCursor || loading}
            className={cn(
              "h-10 px-4 rounded-md border border-border text-sm font-medium inline-flex items-center gap-2",
              !nextCursor || loading ? "opacity-50 cursor-not-allowed" : "hover:bg-muted"
            )}
          >
            Next
            <ArrowRight className="h-4 w-4" />
          </button>
        </div>
      </div>
    </div>
  );
}